<template>
  <div class="product-item" :class="itemTypeClass">
    <div class="product-item__inner" @click="toggle($event)">
      <LockIcon :published-to="item?.publishedTo" />
      <div class="product-item__image-holder">
        <div class="product-item__image-box">
          <img
            :src="imagePath"
            :alt="setProperFieldExternalFirst(image, 'title') || setProperFieldExternalFirst(item, 'title') || 'image'"
            class="product-item__image"
          />
        </div>
      </div>
      <div class="product-item__content">
        <b class="product-item__title">{{ setProperFieldExternalFirst(item, 'title') }}</b>
        <div class="product-item__bar">
          <span class="icon-arrow-expand"></span>
        </div>
      </div>
    </div>
    <div class="product-item__triangle"><div></div></div>
  </div>
</template>

<script setup lang="ts">
  import { useNuxtApp } from 'nuxt/app';
  import { useMainStore } from '@/stores/main';
  import { FALLBACK_IMAGE_PATH } from '~/utils/imageFallback';

  // Type definitions for product item
  interface ProductItemResource {
    fileId?: string;
    fileName?: string;
    mimeType?: string;
    publishedTo?: number;
  }

  interface ProductItemData {
    external?: {
      title?: string;
      [key: string]: any;
    };
    internal?: {
      title?: string;
      [key: string]: any;
    };
    publishedTo?: number;
    resources?: ProductItemResource[];
    [key: string]: any;
  }

  const props = defineProps<{
    index?: number;
    item?: ProductItemData;
  }>();

  const emit = defineEmits<{
    toggle: [data: { index: number; item: ProductItemData }];
  }>();

  const nuxtApp = useNuxtApp();
  const mainStore = useMainStore();
  const baseUrlMedia = computed(() => mainStore.baseUrlMedia);

  const imgRatio = '?width=212&height=212';
  const imgPreview = '/Preview?mimetype=image/jpg';

  // Local helper function (mirrors filter util)
  function setProperFieldExternalFirst(value: any, field: string): string {
    if (!value) return '';
    return value.external && value.external[field]
      ? value.external[field]
      : value.internal && value.internal[field]
        ? value.internal[field]
        : '';
  }

  const image = computed(() => {
    if (props.item?.resources && props.item.resources[0]) {
      let target = props.item.resources[0];
      const img = props.item.resources.find(
        (x) => x.mimeType && x.mimeType.indexOf('image/') > -1 && x.mimeType.indexOf('dwg') < 0 && x.publishedTo == 1
      );
      if (img) {
        target = img;
      } else {
        const pdf = props.item.resources.find((x) => x.mimeType && x.mimeType.indexOf('/pdf') > -1);
        if (pdf) {
          target = pdf;
        }
      }
      return target;
    } else {
      return { fileId: 'noFileId', fileName: 'noFileName', mimeType: '' };
    }
  });

  const imagePath = computed(() => {
    if (image.value.fileId === 'noFileId' || image.value.fileName === 'noFileName') {
      return FALLBACK_IMAGE_PATH;
    }
    if (image.value.mimeType && image.value.mimeType.indexOf('application') > -1) {
      return baseUrlMedia.value + image.value.fileId + '/' + image.value.fileName + imgPreview;
    } else {
      return baseUrlMedia.value + image.value.fileId + '/' + image.value.fileName + imgRatio;
    }
  });

  const itemTypeClass = computed(() => {
    let itemType = props.item?.publishedTo;
    switch (itemType) {
      case 1:
        return 'product-item_external';
      case 2:
        return 'product-item_internal';
      case 3:
        return 'product-item_service';
      default:
        return '';
    }
  });

  function pushGtmProductClick(productTitle: string): void {
    if (import.meta.client) {
      try {
        if ((nuxtApp as any)?.$gtm && typeof (nuxtApp as any).$gtm.pushEvent === 'function') {
          (nuxtApp as any).$gtm.pushEvent({
            productTitle,
            event: 'productClickView'
          });
        } else if (typeof window !== 'undefined' && (window as any).dataLayer && typeof (window as any).dataLayer.push === 'function') {
          (window as any).dataLayer.push({
            productTitle,
            event: 'productClickView'
          });
        }
      } catch (_) {
        // no-op: analytics failures must not break UX
      }
    }
  }

  function toggle(event: Event): void {
    let productTitle = props.item?.external?.title || props.item?.internal?.title || '';
    // Safe GTM push (no-op when GTM not available)
    pushGtmProductClick(productTitle);
    emit('toggle', {
      index: props.index || 0,
      item: props.item || {}
    });
  }
</script>
